/**
 * What a product sells for, how it is packed, and whether a sample can be had.
 *
 * Room to Revenue A1 (docs/room-to-revenue.md §2). Each variant stores its own
 * `commerce.price / pack / sample`; the API resolves what a shopper actually
 * sees into the read-only `pricing` (manual > shop > import > page >
 * attributes, pms-api-v2 product-pricing.ts). The backoffice edits the stored
 * fields and shows the resolved ones with where they came from. `pricing` is
 * never sent back.
 *
 * Money rule: never invent a number. Missing data is null or absent, never a
 * plausible zero.
 */

export const PRICE_UNITS = ['m2', 'box', 'piece', 'unit', 'linear_m', 'roll'] as const;
export type PriceUnit = (typeof PRICE_UNITS)[number];

/** Where a price came from, highest precedence first. */
export const PRICE_SOURCES = ['manual', 'shop', 'import', 'page', 'attributes'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

/** The currencies offered in the editor. A stored one outside the list is still shown. */
export const PRICE_CURRENCIES = ['EUR', 'GBP', 'USD', 'CHF', 'CAD', 'AUD', 'MAD'] as const;

/** Only a starting value for the currency picker; never written without the vendor typing a price. */
export const DEFAULT_PRICE_CURRENCY = 'EUR';

/** `commerce.price` as stored. */
export interface ProductPrice {
  amount: number;
  /** ISO 4217, upper case. */
  currency: string;
  unit?: PriceUnit;
  /** The struck-through price, above `amount`. */
  compareAt?: number;
  source: PriceSource;
  updatedAt?: string;
}

/** `commerce.pack` as stored. */
export interface ProductPack {
  m2PerBox?: number;
  piecesPerBox?: number;
  source?: PriceSource;
}

/** `commerce.sample` as stored. `price` 0 = free; absent = not said. */
export interface ProductSample {
  available: boolean;
  price?: number;
  currency?: string;
}

/** `pricing.price`: what the API resolved for shoppers. Read-only. */
export interface ResolvedPrice {
  amount: number;
  currency: string;
  unit: PriceUnit;
  source: PriceSource;
  compareAt?: number;
  updatedAt?: string;
  /** Nobody stated the unit; m² was assumed for a surface. */
  unitAssumed?: boolean;
}

/** `pricing.pack`. Read-only. */
export interface ResolvedPack {
  m2PerBox?: number;
  piecesPerBox?: number;
  source: PriceSource;
}

export type ResolvedSample = ProductSample;

/** The read-only `pricing` the API computes on every product it returns. */
export interface ProductPricing {
  price: ResolvedPrice | null;
  pack: ResolvedPack | null;
  sample: ResolvedSample | null;
}

type Translate = (key: string, defaultValue?: string) => string;

// ─────────────────────────────────────────────────────────────────────────────
// Reading numbers and units
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A number as people type it: 24.90, "24,90", "1 299,00", "1.299,00",
 * "1,299.00", "24,90 €". Undefined for anything that is not one number.
 * Negative numbers are refused: no price, box or sample costs less than nothing.
 */
export function parseDecimal(raw: unknown): number | undefined {
  if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? raw : undefined;
  if (typeof raw !== 'string') return undefined;
  const text = raw.trim();
  if (!text || /-/.test(text)) return undefined;
  let digits = text.replace(/[\s  ]/g, '').replace(/[^\d.,]/g, '');
  if (!/\d/.test(digits)) return undefined;
  // Anything but digits, separators, spaces and a currency mark is not a number.
  if (/[a-z]/i.test(text.replace(/(eur|gbp|usd|chf|cad|aud|mad)/gi, ''))) return undefined;

  const lastComma = digits.lastIndexOf(',');
  const lastDot = digits.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    // Whichever comes last is the decimal separator: 1.299,00 or 1,299.00.
    digits = lastComma > lastDot
      ? digits.replace(/\./g, '').replace(',', '.')
      : digits.replace(/,/g, '');
  } else if (lastComma > -1) {
    // 24,90 is a decimal; 1,299 (one comma, three digits after) is thousands.
    const decimals = digits.length - lastComma - 1;
    digits = decimals === 3 && digits.indexOf(',') === lastComma
      ? digits.replace(',', '')
      : digits.replace(',', '.');
  }
  if ((digits.match(/\./g) || []).length > 1) return undefined;
  const value = Number(digits);
  return Number.isFinite(value) ? value : undefined;
}

/** Money is kept to the cent. */
export const roundMoney = (n: number): number => Math.round(n * 100) / 100;

/** An ISO currency code, upper case, or undefined. */
export function normalizeCurrency(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const code = raw.trim().toUpperCase();
  if (code === '€') return 'EUR';
  if (code === '£') return 'GBP';
  return /^[A-Z]{3}$/.test(code) ? code : undefined;
}

/**
 * What a price is per, from however a spreadsheet wrote it: m2, m², sqm, box,
 * carton, boîte, piece, pièce, unit, linear_m, ml, roll, rouleau. Undefined
 * when the words say none of these.
 */
export function normalizePriceUnit(raw: unknown): PriceUnit | undefined {
  if (typeof raw !== 'string') return undefined;
  const text = raw.trim().toLowerCase();
  if (!text) return undefined;
  if ((PRICE_UNITS as readonly string[]).includes(text)) return text as PriceUnit;
  if (/^(roll|rolls|rouleau|rouleaux|rollo|rollos)$/.test(text)) return 'roll';
  if (/^(ml|lm|linear[ _-]?m(etre|eter)?s?|m(e|è)tre[s]? lin(e|é)aire[s]?|metro lineal)$/.test(text)) return 'linear_m';
  if (/^(m2|m²|sqm|sq ?m|square ?met(re|er)s?|m(e|è)tre[s]? carr(e|é)[s]?|metro cuadrado)$/.test(text)) return 'm2';
  if (/^(box|boxes|carton|cartons|bo(i|î)te|bo(i|î)tes|caja|cajas|pack|paquet)$/.test(text)) return 'box';
  if (/^(piece|pieces|pièce|pièces|pc|pcs|pieza|piezas)$/.test(text)) return 'piece';
  if (/^(unit|units|unité|unités|unidad|unidades|each)$/.test(text)) return 'unit';
  return undefined;
}

/** A stored price that can be shown or quoted: a positive amount and a currency. */
export function isUsablePrice(price: Partial<ProductPrice> | null | undefined): price is ProductPrice {
  return !!price
    && typeof price.amount === 'number' && Number.isFinite(price.amount) && price.amount > 0
    && !!normalizeCurrency(price.currency);
}

/** A stored pack that says something. */
export function isUsablePack(pack: ProductPack | null | undefined): pack is ProductPack {
  return !!pack && ((pack.m2PerBox ?? 0) > 0 || (pack.piecesPerBox ?? 0) > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Showing them
// ─────────────────────────────────────────────────────────────────────────────

const LOCALES = ['en', 'fr', 'es', 'ar'];
const localeOf = (lang?: string) => {
  const two = (lang || 'en').slice(0, 2);
  return LOCALES.includes(two) ? two : 'en';
};

/**
 * "€45.90", "€45", "45,90 €" — cents only when there are some. A code Intl does
 * not know is written after the number rather than dropped.
 */
export function formatAmount(amount: number, currency: string, lang?: string): string {
  const cents = !Number.isInteger(roundMoney(amount));
  const digits = cents ? 2 : 0;
  try {
    return new Intl.NumberFormat(localeOf(lang), {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch {
    const number = new Intl.NumberFormat(localeOf(lang), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
    return `${number} ${currency.toUpperCase()}`;
  }
}

/** "€45.90 / m²". Without a unit label, the amount alone. */
export function formatPrice(
  price: { amount: number; currency: string; unit?: PriceUnit | null },
  lang?: string,
  unitLabel?: (unit: PriceUnit) => string,
): string {
  const amount = formatAmount(price.amount, price.currency, lang);
  const unit = price.unit && unitLabel ? unitLabel(price.unit) : '';
  return unit ? `${amount} / ${unit}` : amount;
}

const UNIT_SHORT_FALLBACK: Record<PriceUnit, string> = {
  m2: 'm²', box: 'box', piece: 'piece', unit: 'unit', linear_m: 'lin. m', roll: 'roll',
};
const UNIT_LONG_FALLBACK: Record<PriceUnit, string> = {
  m2: 'Square metre (m²)', box: 'Box', piece: 'Piece', unit: 'Unit', linear_m: 'Linear metre', roll: 'Roll',
};

/** "m²" (short, after a price) or "Square metre (m²)" (long, in a picker). */
export function priceUnitLabel(t: Translate, unit: PriceUnit, form: 'short' | 'long' = 'short'): string {
  return form === 'short'
    ? t(`PRODUCT_PRICING.UNIT_SHORT.${unit.toUpperCase()}`, UNIT_SHORT_FALLBACK[unit])
    : t(`PRODUCT_PRICING.UNIT.${unit.toUpperCase()}`, UNIT_LONG_FALLBACK[unit]);
}

const SOURCE_FALLBACK: Record<PriceSource, string> = {
  manual: 'Set by you',
  shop: 'From your shop',
  page: 'Read from your website',
  import: 'From your import',
  attributes: 'From your product details',
};

/** The quiet badge that says where a price or a pack came from. */
export function priceSourceLabel(t: Translate, source: PriceSource): string {
  return t(`PRODUCT_PRICING.SOURCE.${source.toUpperCase()}`, SOURCE_FALLBACK[source]);
}

/** The usual unit for a product when nobody said: m² for a surface, otherwise the item. */
export function defaultPriceUnit(isSurface: boolean): PriceUnit {
  return isSurface ? 'm2' : 'unit';
}
